import React, { useEffect } from 'react';
import { i18n as _i18n } from "@lingui/core";
import { useAppConfiguration } from '@wkda/funnel-components';
import { GoogleRating, GoogleRatingStars, GoogleRatingFigures, GoogleRatingLogo, ReviewsIndicators, GoogleReviewCard, GoogleReviewsSummaryCard, GoogleReviewHeaderNav } from './components';
import { useScrollNavigation } from './hooks/useScrollNavigation';
import { useReviews } from './hooks/useReviews';
import { useModalScrollRecalculation } from './hooks/useModalScrollRecalculation';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export function GoogleRatingsDisplay(_ref) {
  let {
    variant = 'default',
    className = '',
    onClick = () => {}
  } = _ref;
  const baseClass = 'google-ratings-display';
  const modifierClass = `${baseClass}--${variant}`;
  return /*#__PURE__*/_jsxs("div", {
    className: `${baseClass} ${modifierClass} ${className}`,
    onClick: onClick,
    children: [variant === 'default' ? /*#__PURE__*/_jsx(GoogleRatingLogo, {}) : /*#__PURE__*/_jsx(GoogleRating, {}), /*#__PURE__*/_jsx(GoogleRatingStars, {
      variant: variant
    }), /*#__PURE__*/_jsx(GoogleRatingFigures, {
      variant: variant
    })]
  });
}
export default function GoogleRatingsReviews() {
  // Use the custom hook to generate reviews
  const appConfig = useAppConfiguration();
  const {
    hasGoogleReviewsSection = false,
    randomGoogleReviewsCount = null,
    showGoogleReviewsHeaderNav = false,
    showReviewSummaryCard = true
  } = appConfig?.marketingConfig || {};
  const useRandomGoogleReviews = randomGoogleReviewsCount !== null && typeof randomGoogleReviewsCount === 'number';
  const reviews = useReviews(useRandomGoogleReviews, randomGoogleReviewsCount);

  // Use the custom scroll navigation hook
  const scrollNavigation = useScrollNavigation(reviews.length + (showReviewSummaryCard ? 1 : 0)); // +1 for Google summary card if enabled
  // Handle modal/popup scenarios
  useModalScrollRecalculation(hasGoogleReviewsSection, scrollNavigation.forceRecalculation);
  const renderStars = rating => {
    const starElements = [];
    const filledStars = Math.floor(rating);
    const totalStars = 5;
    for (let i = 0; i < totalStars; i++) {
      starElements.push(/*#__PURE__*/_jsx("span", {
        className: `google-ratings-reviews__star ${i < filledStars ? 'google-ratings-reviews__star--filled' : 'google-ratings-reviews__star--empty'}`,
        "aria-hidden": "true",
        children: "\u2605"
      }, i));
    }
    return /*#__PURE__*/_jsx("div", {
      className: "google-ratings-reviews__star-rating",
      role: "img",
      "aria-label": `${rating} ${_i18n._(
      /*i18n*/
      {
        id: 'carlead.ratings.reviews.google.aria.stars'
      })}`,
      children: starElements
    });
  };
  return /*#__PURE__*/_jsxs("section", {
    className: "google-ratings-reviews",
    "aria-labelledby": "ratings-heading",
    children: [/*#__PURE__*/_jsxs("header", {
      className: "google-ratings-reviews__header",
      children: [/*#__PURE__*/_jsx("img", {
        src: _i18n._(
        /*i18n*/
        {
          id: 'carlead.wkda.blueOrange.domain.logo.src'
        }),
        alt: _i18n._(
        /*i18n*/
        {
          id: 'carlead.ratings.reviews.google.aria.header_logo'
        }),
        className: "google-ratings-reviews__header-logo",
        loading: "eager"
      }), /*#__PURE__*/_jsx(GoogleRatingsDisplay, {
        variant: "compact",
        className: "google-ratings-reviews__header-content"
      }), showGoogleReviewsHeaderNav && /*#__PURE__*/_jsx(GoogleReviewHeaderNav, {
        scrollNavigation: scrollNavigation
      })]
    }), /*#__PURE__*/_jsxs("div", {
      className: "google-ratings-reviews__content",
      children: [/*#__PURE__*/_jsx("h2", {
        id: "ratings-heading",
        className: "google-ratings-reviews__title",
        children: _i18n._(
        /*i18n*/
        {
          id: 'carlead.ratings.reviews.google.title'
        })
      }), /*#__PURE__*/_jsxs("div", {
        ref: scrollNavigation.containerRef,
        className: "google-ratings-reviews__container",
        onScroll: scrollNavigation.handleScroll,
        role: "region",
        "aria-label": _i18n._(
        /*i18n*/
        {
          id: 'carlead.ratings.reviews.google.title'
        }),
        "aria-live": "polite",
        children: [reviews.map((review, index) => /*#__PURE__*/_jsx(GoogleReviewCard, {
          review: review,
          index: index,
          renderStars: renderStars
        }, review.id)), showReviewSummaryCard && /*#__PURE__*/_jsx(GoogleReviewsSummaryCard, {})]
      }), /*#__PURE__*/_jsx(ReviewsIndicators, {
        currentIndex: scrollNavigation.currentIndex,
        maxScrollIndex: scrollNavigation.maxScrollIndex,
        containerRef: scrollNavigation.containerRef,
        handleScroll: scrollNavigation.handleScroll,
        scrollToCard: scrollNavigation.scrollToCard,
        handleKeyDown: scrollNavigation.handleKeyDown,
        reviews: reviews
      })]
    })]
  });
}